import {SOURCE_WEATHERFLOW} from '@smartmixin/core';

export const migrations = {
  1: (state) => {
    return {
      ...state,
      prefs: {
        ...state.prefs,
        smallDecimals: true,
      },
    };
  },
  2: (state) => {
    return {
      ...state,
      prefs: {
        ...state.prefs,
        windDirectionConvention: 'oceanographic',
      },
    };
  },
  3: (state) => {
    const {stationDetailList, ...weather} = state.weather;
    return {...state, weather};
  },
  4: (state) => {
    return {
      ...state,
      prefs: {
        ...state.prefs,
        lightningLayer: true,
      },
    };
  },
  // Mirror of mobile's migration 31. `user_data` reached the backend on
  // 2023-03-13 and nothing ever normalised the persisted `link`, so a station
  // stored before then rehydrates with a link object that has no such key —
  // which crashed the station list on mobile (SMARTMIXIN-IOS-6R). The webapp
  // persists `weather` to localStorage under the same shape and had no
  // equivalent heal, so the 2023 shape would have lived there indefinitely.
  // The read sites are guarded now; this stops the shape propagating. `{}` is
  // what the backend stores for a station with no user overrides.
  5: (state) => {
    const stationList = state.weather?.stationList;
    if (!Array.isArray(stationList)) {
      return state;
    }
    return {
      ...state,
      weather: {
        ...state.weather,
        stationList: stationList.map((station) =>
          station?.link && !station.link.user_data
            ? {...station, link: {...station.link, user_data: {}}}
            : station,
        ),
      },
    };
  },
  // #190: `uiInteraction` (`alert`, `upgradeRequired`) was written to storage
  // from the 2024-02-01 (e1e72b399) rename until the blacklist was corrected
  // alongside this migration. Blacklisting is a WRITE-path filter only --
  // `passWhitelistBlacklist` is consulted in createPersistoid and nowhere in
  // getStoredState or persistReducer -- so the stored blob would still be
  // handed to the state reconciler one last time. This strips the key instead,
  // so no stale ERROR toast replays on the first load after the fix.
  6: (state) => {
    const {uiInteraction, ...rest} = state;
    return rest;
  },
  // #189: `SOURCE_WEATHERFLOW` was appended to the shared
  // `stationMapFilter.source` default in
  // `packages/core/src/store/weather/index.ts` by 0c4ca073f (2024-04-15) with
  // no migration on either app. `initialState` is not what an existing install
  // reads: `autoMergeLevel2` replaces `weather.stationMapFilter` wholesale from
  // storage because it sits at depth 2, and `getStationMapSourceFilter` only
  // falls back to DEFAULT_MAP_SOURCE_FILTER when the stored value is *absent*.
  // A seven-element array is truthy, so it is returned verbatim and
  // `WeatherStationOverlay` filters Weatherflow off the map permanently.
  //
  // Weatherflow only, unlike mobile's migration 33 -- but for a different
  // reason than mobile's. The webapp has NO filter UI: `setStationMapSource` is
  // dispatched from exactly two places and both are mobile
  // (FilterContainer.tsx, StationFilter.tsx). A webapp user cannot uncheck a
  // source, so this value is always whatever the default was when the blob was
  // written, and full reconciliation against the default would in fact be safe
  // here. It simply is not needed: this store first shipped 2024-01-15
  // (0882b28de), by which point Davis and Wunderground had been in the default
  // for over two years, so every webapp blob ever written already carried them.
  // Weatherflow is genuinely the only gap, and the affected window is blobs
  // written between 2024-01-15 and 2024-04-15.
  7: (state) => {
    const filter = state.weather && state.weather.stationMapFilter;

    // An absent filter is left absent: the selector's `|| DEFAULT` fallback
    // already answers correctly for that shape.
    if (!filter || !Array.isArray(filter.source)) {
      return state;
    }

    if (filter.source.includes(SOURCE_WEATHERFLOW)) {
      return state;
    }

    return {
      ...state,
      weather: {
        ...state.weather,
        stationMapFilter: {
          ...filter,
          source: filter.source.concat([SOURCE_WEATHERFLOW]),
        },
      },
    };
  },
  // NAM left the catalogue when RRFS replaced it (NOAA retires NAM
  // 2026-10-14). A per-station override stores a CATALOGUE key, and
  // `resolveSelectedForecastModel` returns the `find` result with no fallback
  // on that branch — so an override still pointing at NAM resolves to
  // `undefined` and the station shows no model until the user re-picks one.
  // Dropping it restores the normal default. Mirrors mobile migration 35.
  // Pins need no equivalent: `reorderKnownPins` deliberately keeps an unknown
  // pin so it survives a build that cannot draw it.
  8: (state) => {
    const drop = (overrides?: Record<string, string>) =>
      Object.fromEntries(
        Object.entries(overrides ?? {}).filter(
          ([, model]) => model !== 'NAM_CONUS' && model !== 'NAM_NORTH_AMERICA',
        ),
      );
    return {
      ...state,
      weather: {
        ...state.weather,
        selectedForecastModel: drop(state.weather?.selectedForecastModel),
      },
      location: {
        ...state.location,
        selectedForecastModel: drop(state.location?.selectedForecastModel),
      },
    };
  },
  // FE #327: the /map layer toggles moved out of the webapp's own `prefs`
  // into core's `mapPreferences`, which the unified map (and later mobile)
  // reads. Irreversible: the old keys are stripped, so a rollback to a v8
  // build reads every layer as shown (its selectors default `?? true`). A
  // re-upgrade after that does not bring the toggles back either: v8's
  // combineReducers drops the unknown `mapPreferences` key, so the blob it
  // writes holds neither the old keys nor the new ones, and this migration
  // then sets every layer to shown.
  //
  // A toggle never stored -- `lightningLayer` only arrived with migration 4 --
  // keeps the old selectors' answer, shown. A blob with no `prefs` at all is
  // real: the E2E seed writes a version-6 blob holding only `user`. It gets
  // no `prefs` key invented for it. Whatever `mapPreferences` already holds
  // besides `observations` is kept.
  9: (state) => {
    const {stationLayer, rainRadarLayer, lightningLayer, ...prefs} =
      state.prefs ?? {};
    return {
      ...state,
      ...(state.prefs ? {prefs} : {}),
      mapPreferences: {
        ...state.mapPreferences,
        observations: {
          stations: stationLayer ?? true,
          rainRadar: rainRadarLayer ?? true,
          lightning: lightningLayer ?? true,
        },
      },
    };
  },
  // Vincent, 2026-10-20: the forecast map's wind barbs start hidden (the
  // wind particles carry the flow), and the map draws the light basemap
  // whatever the app theme. A stored blob holds the old defaults -- no
  // hidden layer, Auto -- as values, indistinguishable from a choice, so
  // they are rewritten: the barbs join the hidden layers (a stored `[]` is
  // the old default and a user's off-then-on alike, so it is overridden)
  // and any Map theme but Dark becomes Light. The few who picked Auto by hand pick it
  // again. Mirrors mobile migration 38.
  //
  // The literals are frozen here on purpose: core's
  // DEFAULT_HIDDEN_FORECAST_VECTOR_LAYERS may move on, this migration may
  // not. A blob with no `mapPreferences`, or no `forecast` in it, gets none
  // invented: autoMergeLevel2 fills a missing one from the initial state,
  // which already holds the new defaults.
  10: (state) => {
    if (!state.mapPreferences) {
      return state;
    }
    const {forecast, basemapTheme} = state.mapPreferences;
    const hidden = Array.isArray(forecast?.hiddenVectorLayers)
      ? forecast.hiddenVectorLayers
      : [];
    return {
      ...state,
      mapPreferences: {
        ...state.mapPreferences,
        ...(forecast
          ? {
              forecast: {
                ...forecast,
                hiddenVectorLayers: hidden.includes('barbs:10m wind')
                  ? hidden
                  : [...hidden, 'barbs:10m wind'],
              },
            }
          : {}),
        basemapTheme: basemapTheme === 'dark' ? 'dark' : 'light',
      },
    };
  },
  // FE #405: the Netatmo public-station map went with react-native-maps
  // (!967), and its settings left core's `netatmoWeather` slice. The webapp
  // never showed that map, but the keys sat in the slice's initial state, so
  // every blob holding the slice holds them. A stored slice's keys are copied
  // over the initial state by autoMergeLevel2 whether the slice still declares
  // them or not, so without this they would be rehydrated and written back
  // for good. Mirrors mobile migration 40.
  //
  // A blob with no `netatmoWeather` (the E2E seed's) gets none invented:
  // autoMergeLevel2 fills it from the initial state.
  11: (state) => {
    if (!state.netatmoWeather) {
      return state;
    }
    const {
      mapMarkers,
      mapFilterStations,
      mapRainMeasurement,
      mapWindMeasurement,
      ...netatmoWeather
    } = state.netatmoWeather;
    return {...state, netatmoWeather};
  },
};
